package diana.soleil.design.iraniancanadienbazzar.core.data.fake

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Fact
import diana.soleil.design.iraniancanadienbazzar.core.model.FactCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.Joke
import diana.soleil.design.iraniancanadienbazzar.core.model.JokeCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.JokeMaturity
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.PoemForm
import diana.soleil.design.iraniancanadienbazzar.core.model.Poet
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeDifficulty
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeIngredient
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeRegion
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeStep
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import diana.soleil.design.iraniancanadienbazzar.core.model.Story
import diana.soleil.design.iraniancanadienbazzar.core.model.StoryRegion
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map

/**
 * In-memory [GemsRepository] for previews/tests and the Developer Menu's mock-data mode. Stateful:
 * likes/saves/opt-in mutate observable flows so the whole Hidden Gems flow (like counts, saved
 * rail, age gate) is exercisable with zero backend traffic. [currentUid] defaults to a fake
 * signed-in user in mock mode so the like/save affordances are testable.
 */
class FakeGemsRepository(
    seedJokes: List<Joke> = demoJokes,
    seedRecipes: List<Recipe> = demoRecipes,
    seedFacts: List<Fact> = demoFacts,
    seedTales: List<Tale> = demoTales,
    seedPoets: List<Poet> = demoPoets,
    seedPoems: List<Poem> = demoPoems,
    seedStories: List<Story> = demoStories,
    override val currentUid: String? = MockScenarios.MOCK_UID,
) : GemsRepository {

    private val jokes = MutableStateFlow(seedJokes)
    private val recipes = MutableStateFlow(seedRecipes)
    private val facts = MutableStateFlow(seedFacts)
    private val tales = MutableStateFlow(seedTales)
    private val likedJokes = MutableStateFlow<Set<String>>(emptySet())
    private val likedRecipes = MutableStateFlow<Set<String>>(emptySet())
    private val likedFacts = MutableStateFlow<Set<String>>(emptySet())
    private val likedTales = MutableStateFlow<Set<String>>(emptySet())
    private val poets = MutableStateFlow(seedPoets)
    private val poems = MutableStateFlow(seedPoems)
    private val stories = MutableStateFlow(seedStories)
    private val likedPoems = MutableStateFlow<Set<String>>(emptySet())
    private val likedStories = MutableStateFlow<Set<String>>(emptySet())
    private val savedGems = MutableStateFlow<Set<SavedGem>>(emptySet())
    private val adultOptIn = MutableStateFlow<Boolean?>(false)

    override fun observeJokes(includeAdult: Boolean, limit: Int): Flow<DataResult<List<Joke>>> =
        combine(jokes, likedJokes) { all, _ ->
            DataResult.Success(
                all.filter { includeAdult || it.maturity == JokeMaturity.Clean }
                    .sortedByDescending { it.publishedAtMillis }
                    .take(limit),
            ) as DataResult<List<Joke>>
        }

    override fun observeRecipes(limit: Int): Flow<DataResult<List<Recipe>>> =
        recipes.map { all ->
            DataResult.Success(all.sortedByDescending { it.publishedAtMillis }.take(limit))
                as DataResult<List<Recipe>>
        }

    override fun observeFacts(limit: Int): Flow<DataResult<List<Fact>>> =
        combine(facts, likedFacts) { all, _ ->
            DataResult.Success(all.sortedByDescending { it.publishedAtMillis }.take(limit))
                as DataResult<List<Fact>>
        }

    override fun observeTales(limit: Int): Flow<DataResult<List<Tale>>> =
        combine(tales, likedTales) { all, _ ->
            DataResult.Success(all.sortedByDescending { it.publishedAtMillis }.take(limit))
                as DataResult<List<Tale>>
        }

    override suspend fun getRecipe(recipeId: String): DataResult<Recipe> =
        recipes.value.firstOrNull { it.id == recipeId }
            ?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override suspend fun getTale(taleId: String): DataResult<Tale> =
        tales.value.firstOrNull { it.id == taleId }
            ?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override fun observeLikedJokeIds(): Flow<Set<String>> = likedJokes

    override fun observeLikedRecipeIds(): Flow<Set<String>> = likedRecipes

    override fun observeLikedFactIds(): Flow<Set<String>> = likedFacts

    override fun observeLikedTaleIds(): Flow<Set<String>> = likedTales

    override suspend fun setJokeLiked(jokeId: String, liked: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        likedJokes.value = likedJokes.value.toMutableSet().apply { if (liked) add(jokeId) else remove(jokeId) }
        jokes.value = jokes.value.map {
            if (it.id == jokeId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setRecipeLiked(recipeId: String, liked: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        likedRecipes.value = likedRecipes.value.toMutableSet().apply { if (liked) add(recipeId) else remove(recipeId) }
        recipes.value = recipes.value.map {
            if (it.id == recipeId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setFactLiked(factId: String, liked: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        likedFacts.value = likedFacts.value.toMutableSet().apply { if (liked) add(factId) else remove(factId) }
        facts.value = facts.value.map {
            if (it.id == factId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setTaleLiked(taleId: String, liked: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        likedTales.value = likedTales.value.toMutableSet().apply { if (liked) add(taleId) else remove(taleId) }
        tales.value = tales.value.map {
            if (it.id == taleId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
        }
        return DataResult.Success(Unit)
    }

    override fun observePoets(limit: Int): Flow<DataResult<List<Poet>>> =
        poets.map { all ->
            DataResult.Success(all.sortedBy { it.orderIndex }.take(limit)) as DataResult<List<Poet>>
        }

    override fun observePoems(poetId: String?, limit: Int): Flow<DataResult<List<Poem>>> =
        poems.map { all ->
            DataResult.Success(
                all.filter { poetId == null || it.poetId == poetId }.take(limit),
            ) as DataResult<List<Poem>>
        }

    override suspend fun getPoem(poemId: String): DataResult<Poem> =
        poems.value.firstOrNull { it.id == poemId }
            ?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override fun observeStories(limit: Int): Flow<DataResult<List<Story>>> =
        stories.map { all ->
            DataResult.Success(all.sortedByDescending { it.publishedAtMillis }.take(limit))
                as DataResult<List<Story>>
        }

    override suspend fun getStory(storyId: String): DataResult<Story> =
        stories.value.firstOrNull { it.id == storyId }
            ?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override fun observeLikedPoemIds(): Flow<Set<String>> = likedPoems

    override fun observeLikedStoryIds(): Flow<Set<String>> = likedStories

    override suspend fun setPoemLiked(poemId: String, liked: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        likedPoems.value = likedPoems.value.toMutableSet().apply { if (liked) add(poemId) else remove(poemId) }
        poems.value = poems.value.map {
            if (it.id == poemId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setStoryLiked(storyId: String, liked: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        likedStories.value = likedStories.value.toMutableSet().apply { if (liked) add(storyId) else remove(storyId) }
        stories.value = stories.value.map {
            if (it.id == storyId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
        }
        return DataResult.Success(Unit)
    }

    override fun observeSavedGemsCloud(): Flow<Set<SavedGem>?> =
        savedGems.map { if (currentUid == null) null else it }

    override suspend fun setSavedGemCloud(gem: SavedGem, saved: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        savedGems.value = savedGems.value.toMutableSet().apply { if (saved) add(gem) else remove(gem) }
        return DataResult.Success(Unit)
    }

    override fun observeAdultOptIn(): Flow<Boolean?> = adultOptIn

    override suspend fun setAdultOptIn(optedIn: Boolean): DataResult<Unit> {
        if (currentUid == null) return DataResult.Failure(AppError.Unauthorized())
        adultOptIn.value = optedIn
        return DataResult.Success(Unit)
    }

    companion object {
        private const val DAY_MS = 86_400_000L
        private const val SEED_NOW_MS = 1_784_000_000_000L

        /**
         * Poets whose work is public domain in Canada (life + 50 years). The FA lines are the
         * original verse; en/fr are plain translations written for this app, so no third party's
         * text or translation is reproduced.
         */
        val demoPoets: List<Poet> = listOf(
            Poet(
                id = "poet-hafez",
                name = LocalizedText(en = "Hafez", fr = "Hafez", fa = "حافظ"),
                era = LocalizedText(en = "14th century, Shiraz", fr = "XIVe siècle, Chiraz", fa = "قرن هشتم، شیراز"),
                bio = LocalizedText(
                    en = "The most quoted poet in Persian. Families still open his Divan at random for guidance on Yalda night.",
                    fr = "Le poète le plus cité en persan. On ouvre encore son Divan au hasard, la nuit de Yalda, pour y lire un présage.",
                    fa = "پرنقل‌قول‌ترین شاعر فارسی؛ هنوز شب یلدا دیوانش را برای فال باز می‌کنند.",
                ),
                orderIndex = 1,
                publishedAtMillis = SEED_NOW_MS,
            ),
            Poet(
                id = "poet-saadi",
                name = LocalizedText(en = "Saadi", fr = "Saadi", fa = "سعدی"),
                era = LocalizedText(en = "13th century, Shiraz", fr = "XIIIe siècle, Chiraz", fa = "قرن هفتم، شیراز"),
                bio = LocalizedText(
                    en = "Moralist and traveller. His line about the sons of Adam hangs at the United Nations.",
                    fr = "Moraliste et voyageur. Son vers sur les fils d'Adam est affiché aux Nations Unies.",
                    fa = "شاعر و جهان‌گرد؛ بیت «بنی‌آدم اعضای یک پیکرند» بر دیوار سازمان ملل است.",
                ),
                orderIndex = 2,
                publishedAtMillis = SEED_NOW_MS,
            ),
            Poet(
                id = "poet-khayyam",
                name = LocalizedText(en = "Omar Khayyam", fr = "Omar Khayyam", fa = "عمر خیّام"),
                era = LocalizedText(en = "11th century, Neyshabur", fr = "XIe siècle, Neyshabour", fa = "قرن پنجم، نیشابور"),
                bio = LocalizedText(
                    en = "Astronomer and mathematician whose quatrains keep asking what the hurry is.",
                    fr = "Astronome et mathématicien dont les quatrains demandent toujours pourquoi tant de hâte.",
                    fa = "ستاره‌شناس و ریاضی‌دان؛ رباعی‌هایش هنوز می‌پرسند این‌همه شتاب برای چیست.",
                ),
                orderIndex = 3,
                publishedAtMillis = SEED_NOW_MS,
            ),
        )

        val demoPoems: List<Poem> = listOf(
            Poem(
                id = "poem-saadi-bani-adam",
                poetId = "poet-saadi",
                title = LocalizedText(en = "The Sons of Adam", fr = "Les fils d'Adam", fa = "بنی‌آدم"),
                body = LocalizedText(
                    fa = "بنی‌آدم اعضای یک پیکرند\nکه در آفرینش ز یک گوهرند\nچو عضوی به درد آورد روزگار\nدگر عضوها را نماند قرار",
                    en = "The children of Adam are limbs of one body,\nmade from a single essence.\nWhen time brings pain to one limb,\nthe others cannot rest.",
                    fr = "Les enfants d'Adam sont les membres d'un même corps,\nissus d'une seule essence.\nQuand le temps blesse un membre,\nles autres ne trouvent plus de repos.",
                ),
                note = LocalizedText(
                    en = "From the Gulistan (1258). Inscribed at the United Nations.",
                    fr = "Tiré du Golestan (1258). Inscrit aux Nations Unies.",
                    fa = "از گلستان (۶۵۶ ق). بر دیوار سازمان ملل نوشته شده است.",
                ),
                form = PoemForm.Qete,
                likeCount = 128,
                publishedAtMillis = SEED_NOW_MS,
            ),
            Poem(
                id = "poem-hafez-saqi",
                poetId = "poet-hafez",
                title = LocalizedText(en = "Pass the Cup", fr = "Passe la coupe", fa = "الا یا ایها الساقی"),
                body = LocalizedText(
                    fa = "الا یا ایها الساقی ادر کأسا و ناولها\nکه عشق آسان نمود اول ولی افتاد مشکل‌ها",
                    en = "Cup-bearer, turn the cup and pass it round —\nlove looked easy at first, then the difficulties came.",
                    fr = "Échanson, fais tourner la coupe et sers-la —\nl'amour semblait facile d'abord, puis les difficultés sont venues.",
                ),
                fal = LocalizedText(
                    en = "What you want will not come the easy way it first seemed, and it is still worth wanting. Keep your cup moving — the hard part is the part that counts.",
                    fr = "Ce que tu veux ne viendra pas par le chemin facile qu'il semblait, et cela vaut encore la peine. Fais tourner ta coupe — la part difficile est celle qui compte.",
                    fa = "آنچه می‌خواهی از راهی که اول آسان می‌نمود نمی‌آید، و همچنان ارزشش را دارد. پیاله را بگردان؛ سختی‌اش همان جایی است که به حساب می‌آید.",
                ),
                form = PoemForm.Ghazal,
                likeCount = 96,
                publishedAtMillis = SEED_NOW_MS - DAY_MS,
            ),
            Poem(
                id = "poem-khayyam-caravan",
                poetId = "poet-khayyam",
                title = LocalizedText(en = "This Caravan of Life", fr = "Cette caravane de la vie", fa = "این قافلهٔ عمر"),
                body = LocalizedText(
                    fa = "این قافلهٔ عمر عجب می‌گذرد\nدریاب دمی که با طرب می‌گذرد",
                    en = "This caravan of life passes strangely by —\nseize the breath that passes in gladness.",
                    fr = "Cette caravane de la vie passe étrangement —\nsaisis l'instant qui passe dans la joie.",
                ),
                form = PoemForm.Robaei,
                likeCount = 74,
                publishedAtMillis = SEED_NOW_MS - 2 * DAY_MS,
            ),
        )

        /** Traditional folk material, retold in our own words — same rights model as the tales. */
        val demoStories: List<Story> = listOf(
            Story(
                id = "story-gilan-rice-moon",
                title = LocalizedText(
                    en = "The Rice Field and the Moon",
                    fr = "La rizière et la lune",
                    fa = "شالیزار و ماه",
                ),
                summary = LocalizedText(
                    en = "A Gilan farmer tries to keep the moon from leaving his flooded field.",
                    fr = "Un fermier du Gilan essaie d'empêcher la lune de quitter sa rizière inondée.",
                    fa = "کشاورزی گیلانی می‌کوشد نگذارد ماه از شالیزار پرآبش برود.",
                ),
                body = LocalizedText(
                    en = "In a village near Lahijan a farmer saw the full moon sitting in his flooded rice field and decided it had come to live with him. He built a low wall of mud so it could not roll away, and told nobody, in case a neighbour claimed a share.\n\nAt dawn the moon was gone. He blamed the wall, the water, then the sky itself — until his grandmother asked whether he had also thought to keep the rain, the mist and the smell of the paddy. He had not. She told him that the things worth having in Gilan all pass through: they are borrowed, never kept.\n\nHe left the wall standing anyway. Every month, for one night, the moon came back to it — and every month he let it go.",
                    fr = "Dans un village près de Lahijan, un fermier vit la pleine lune posée dans sa rizière inondée et décida qu'elle était venue vivre chez lui. Il bâtit un muret de boue pour qu'elle ne roule pas ailleurs, et n'en parla à personne, de peur qu'un voisin n'en réclame une part.\n\nÀ l'aube, la lune avait disparu. Il accusa le mur, puis l'eau, puis le ciel — jusqu'à ce que sa grand-mère lui demande s'il avait aussi songé à garder la pluie, la brume et l'odeur de la rizière. Non. Elle lui dit que tout ce qui vaut la peine, au Gilan, ne fait que passer : on l'emprunte, on ne le garde pas.\n\nIl laissa le muret debout. Chaque mois, une nuit, la lune y revenait — et chaque mois il la laissait partir.",
                    fa = "در روستایی نزدیک لاهیجان، کشاورزی ماه کامل را در شالیزار پرآبش دید و گمان کرد ماه آمده است تا پیش او بماند. دیواری کوتاه از گِل ساخت تا ماه نغلتد و برود، و به کسی چیزی نگفت، مبادا همسایه‌ای سهم بخواهد.\n\nسپیده‌دم ماه رفته بود. اول دیوار را سرزنش کرد، بعد آب را، بعد خود آسمان را — تا مادربزرگش پرسید آیا فکر کرده بود باران و مه و بوی شالیزار را هم نگه دارد؟ نکرده بود. مادربزرگ گفت هر چه در گیلان ارزش داشتن دارد، گذراست: امانت است، نه مِلک.\n\nدیوار را خراب نکرد. هر ماه، یک شب، ماه به آن برمی‌گشت — و هر ماه او می‌گذاشت برود.",
                ),
                region = StoryRegion.Gilan,
                moral = LocalizedText(
                    en = "What is beautiful is borrowed.",
                    fr = "Ce qui est beau n'est qu'emprunté.",
                    fa = "زیبایی امانت است.",
                ),
                likeCount = 31,
                publishedAtMillis = SEED_NOW_MS,
            ),
            Story(
                id = "story-fars-salt-merchant",
                title = LocalizedText(
                    en = "The Salt Merchant's Donkey",
                    fr = "L'âne du marchand de sel",
                    fa = "الاغ نمک‌فروش",
                ),
                summary = LocalizedText(
                    en = "A donkey near Shiraz learns a clever trick, and learns its price.",
                    fr = "Près de Chiraz, un âne apprend une ruse — et en découvre le prix.",
                    fa = "الاغی در نزدیکی شیراز ترفندی زیرکانه می‌آموزد و بهایش را هم می‌فهمد.",
                ),
                body = LocalizedText(
                    en = "A salt merchant crossed a stream every market day. One morning his donkey slipped, and the salt dissolved; the load became light. The donkey remembered.\n\nFrom then on it lay down in the water at every crossing. The merchant said nothing. The next week he loaded the sacks with wool.\n\nThe donkey lay down as usual, the wool drank the river, and it carried triple the weight up the bank. It never lay down again — and the merchant, who had lost a season of salt teaching the lesson, said that a good lesson is always paid for twice.",
                    fr = "Un marchand de sel traversait un ruisseau chaque jour de marché. Un matin, son âne glissa : le sel se dissolut et la charge devint légère. L'âne s'en souvint.\n\nDès lors, il se couchait dans l'eau à chaque traversée. Le marchand ne dit rien. La semaine suivante, il remplit les sacs de laine.\n\nL'âne se coucha comme d'habitude, la laine but la rivière, et il remonta la berge avec le triple du poids. Il ne se coucha plus jamais — et le marchand, qui avait perdu une saison de sel pour donner la leçon, disait qu'une bonne leçon se paie toujours deux fois.",
                    fa = "نمک‌فروشی هر روز بازار از جویی می‌گذشت. صبحی الاغش لغزید، نمک در آب حل شد و بار سبک شد. الاغ این را به یاد سپرد.\n\nاز آن پس سر هر گذر، در آب می‌خوابید. مرد چیزی نگفت. هفتهٔ بعد جوال‌ها را پر از پشم کرد.\n\nالاغ طبق عادت خوابید، پشم آب رودخانه را نوشید و او سه برابر وزن را تا بالای ساحل برد. دیگر هرگز نخوابید — و مرد که یک فصل نمک را خرج این درس کرده بود، می‌گفت درس خوب را همیشه دو بار می‌پردازی.",
                ),
                region = StoryRegion.Fars,
                moral = LocalizedText(
                    en = "Every shortcut is weighed later.",
                    fr = "Tout raccourci se pèse plus tard.",
                    fa = "هر میان‌بُر را بعداً وزن می‌کنند.",
                ),
                likeCount = 22,
                publishedAtMillis = SEED_NOW_MS - DAY_MS,
            ),
        )

        /** Small trilingual demo corpus so mock mode shows a working hub without the backend. */
        val demoJokes: List<Joke> = listOf(
            Joke(
                id = "mock-joke-taarof",
                text = LocalizedText(
                    en = "An Iranian refuses tea three times out of politeness. On the fourth offer, he accepts — and takes the whole samovar.",
                    fr = "Un Iranien refuse le thé trois fois par politesse. À la quatrième offre, il accepte — et repart avec le samovar entier.",
                    fa = "ایرانی سه بار از روی تعارف چای را رد می‌کند. بار چهارم قبول می‌کند — و کل سماور را برمی‌دارد.",
                ),
                category = JokeCategory.Observational,
                tags = listOf("taarof"),
                likeCount = 42,
                publishedAtMillis = SEED_NOW_MS,
            ),
            Joke(
                id = "mock-joke-grandma",
                text = LocalizedText(
                    en = "My grandmother's recipe has three precise measurements: \"a little\", \"enough\", and \"until it looks right\".",
                    fr = "La recette de ma grand-mère a trois mesures précises : « un peu », « assez », et « jusqu'à ce que ça ait l'air bon ».",
                    fa = "دستور آشپزی مادربزرگم سه واحد اندازه‌گیری دقیق دارد: «یه کم»، «به اندازه»، و «تا وقتی که خوب به نظر برسد».",
                ),
                category = JokeCategory.Family,
                tags = listOf("food"),
                likeCount = 17,
                publishedAtMillis = SEED_NOW_MS - DAY_MS,
            ),
        )

        val demoRecipes: List<Recipe> = listOf(
            Recipe(
                id = "mock-recipe-mirza",
                title = LocalizedText(en = "Mirza Ghasemi", fr = "Mirza Ghasemi", fa = "میرزا قاسمی"),
                summary = LocalizedText(
                    en = "Gilan's smoky mashed eggplant with garlic, tomato and egg.",
                    fr = "Purée d'aubergines fumées du Gilan avec ail, tomate et œuf.",
                    fa = "بادمجان کبابی گیلان با سیر، گوجه و تخم‌مرغ.",
                ),
                story = LocalizedText(
                    en = "Named after a 19th-century governor of Rasht.",
                    fr = "Nommé d'après un gouverneur de Racht du XIXe siècle.",
                    fa = "نامش را از حاکم رشت در قرن نوزدهم گرفته است.",
                ),
                region = RecipeRegion.Gilan,
                category = RecipeCategory.Appetizer,
                prepMinutes = 15,
                cookMinutes = 30,
                servings = 4,
                difficulty = RecipeDifficulty.Easy,
                groceryQuery = "persian grocery",
                ingredients = listOf(
                    RecipeIngredient(
                        name = LocalizedText(en = "Large eggplants", fr = "Grosses aubergines", fa = "بادمجان درشت"),
                        quantity = LocalizedText(en = "4", fr = "4", fa = "۴ عدد"),
                    ),
                    RecipeIngredient(
                        name = LocalizedText(en = "Garlic cloves", fr = "Gousses d'ail", fa = "حبه سیر"),
                        quantity = LocalizedText(en = "6, minced", fr = "6, hachées", fa = "۶ حبه، خردشده"),
                    ),
                ),
                steps = listOf(
                    RecipeStep(
                        text = LocalizedText(
                            en = "Char the eggplants over an open flame until the skin blackens.",
                            fr = "Griller les aubergines à la flamme jusqu'à ce que la peau noircisse.",
                            fa = "بادمجان‌ها را روی شعله کباب کنید تا پوست بسوزد.",
                        ),
                    ),
                    RecipeStep(
                        text = LocalizedText(
                            en = "Sauté the garlic, add turmeric and tomato, then the eggplant.",
                            fr = "Faire revenir l'ail, ajouter curcuma et tomate, puis l'aubergine.",
                            fa = "سیر را تفت دهید، زردچوبه و گوجه و سپس بادمجان را اضافه کنید.",
                        ),
                    ),
                ),
                tags = listOf("vegetarian"),
                likeCount = 31,
                publishedAtMillis = SEED_NOW_MS,
            ),
        )

        /** Small trilingual Danestaniha ("did you know?") demo set for mock mode / previews. */
        val demoFacts: List<Fact> = listOf(
            Fact(
                id = "mock-fact-yalda",
                text = LocalizedText(
                    en = "Yalda Night — the longest night of the year — has been celebrated by Iranians for over 2,000 years with pomegranates, watermelon and poetry.",
                    fr = "La nuit de Yalda — la plus longue de l'année — est célébrée par les Iraniens depuis plus de 2 000 ans avec grenades, pastèque et poésie.",
                    fa = "شب یلدا، بلندترین شب سال، بیش از دو هزار سال است که ایرانیان آن را با انار، هندوانه و شعر جشن می‌گیرند.",
                ),
                category = FactCategory.Iran,
                tags = listOf("yalda", "nowruz"),
                likeCount = 88,
                publishedAtMillis = SEED_NOW_MS,
            ),
            Fact(
                id = "mock-fact-diaspora",
                text = LocalizedText(
                    en = "More than 400,000 people of Iranian origin call Canada home, with the largest communities in Toronto, Vancouver and Montreal.",
                    fr = "Plus de 400 000 personnes d'origine iranienne vivent au Canada, les plus grandes communautés se trouvant à Toronto, Vancouver et Montréal.",
                    fa = "بیش از ۴۰۰ هزار نفر از ایرانی‌تباران در کاناداست، با بزرگ‌ترین جامعه‌ها در تورنتو، ونکوور و مونترال.",
                ),
                category = FactCategory.Community,
                source = LocalizedText(en = "Statistics Canada", fr = "Statistique Canada", fa = "آمار کانادا"),
                tags = listOf("diaspora"),
                likeCount = 54,
                publishedAtMillis = SEED_NOW_MS - DAY_MS,
            ),
        )

        /** A trilingual Shahnameh demo tale for mock mode / previews. */
        val demoTales: List<Tale> = listOf(
            Tale(
                id = "mock-tale-kaveh",
                title = LocalizedText(en = "Kaveh the Blacksmith", fr = "Kaveh le forgeron", fa = "کاوهٔ آهنگر"),
                summary = LocalizedText(
                    en = "A blacksmith raises a leather apron on a spear and topples a tyrant.",
                    fr = "Un forgeron hisse son tablier de cuir sur une lance et renverse un tyran.",
                    fa = "آهنگری پیش‌بند چرمی‌اش را بر نیزه می‌کند و ستمگری را سرنگون می‌کند.",
                ),
                body = LocalizedText(
                    en = "When the serpent-king Zahhak demanded the brains of the young to feed the snakes on his shoulders, the blacksmith Kaveh — who had lost seventeen sons — rose in the court and tore up the king's decree. He lifted his leather apron on a spear as a banner, and the people rallied behind it to seek out Fereydun, the rightful heir who would bind Zahhak in a cave beneath Mount Damavand.",
                    fr = "Quand le roi-serpent Zahhak exigea la cervelle des jeunes pour nourrir les serpents de ses épaules, le forgeron Kaveh — qui avait perdu dix-sept fils — se dressa et déchira le décret du roi. Il hissa son tablier de cuir sur une lance en guise d'étendard, et le peuple se rallia pour trouver Fereydoun, l'héritier légitime qui enchaînerait Zahhak dans une grotte sous le mont Damavand.",
                    fa = "چون ضحاکِ ماردوش مغز جوانان را برای مارهای دوشش می‌خواست، کاوهٔ آهنگر که هفده پسر از دست داده بود در بارگاه برخاست و فرمان شاه را درید. پیش‌بند چرمی‌اش را چون درفشی بر نیزه کرد و مردم گرد او جمع شدند تا فریدون، وارث بر‌حق، را بیابند؛ همان که ضحاک را در غاری زیر کوه دماوند به بند کشید.",
                ),
                moral = LocalizedText(
                    en = "One ordinary person's courage can end a tyranny.",
                    fr = "Le courage d'une personne ordinaire peut mettre fin à une tyrannie.",
                    fa = "شجاعت یک انسان عادی می‌تواند به ستم پایان دهد.",
                ),
                characters = listOf("kaveh", "zahhak", "fereydun"),
                likeCount = 73,
                publishedAtMillis = SEED_NOW_MS,
            ),
        )
    }
}
